import axios from 'axios';
import { tmdbCache } from './cache';

const TMDB_BASE_URL = 'https://api.themoviedb.org/3';
const TMDB_IMAGE_BASE_URL = 'https://image.tmdb.org/t/p';

// Use our cached API routes in production, direct TMDB API in development
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/tmdb'
  : TMDB_BASE_URL;

const tmdbApi = axios.create({
  baseURL: API_BASE_URL,
  params: process.env.NODE_ENV !== 'production' ? {
    api_key: process.env.NEXT_PUBLIC_TMDB_API_KEY,
  } : {},
});

// Helper function for cached API requests with Redis
async function cachedRequest<T>(
  type: string,
  contentType: string,
  requestFn: () => Promise<T>,
  id?: string | number
): Promise<T> {
  // Try to get from cache first (checks both memory and Redis)
  const cached = await tmdbCache.get<T>(type, id);
  if (cached) {
    return cached;
  }

  // Make the request and cache the result
  const result = await requestFn();
  await tmdbCache.set(type, id, result, contentType);
  return result;
}

export interface Show {
  id: number;
  name: string;
  poster_path: string | null;
  backdrop_path: string | null;
  overview: string;
  first_air_date: string;
  vote_average: number;
  number_of_seasons?: number;
  seasons?: Season[];
  next_episode_to_air?: Episode | null;
  last_episode_to_air?: Episode | null;
  status?: string;
  networks?: Network[];
}

export interface Season {
  id: number;
  season_number: number;
  name: string;
  episode_count: number;
  air_date: string | null;
  poster_path: string | null;
  overview: string;
}

export interface Episode {
  id: number;
  name: string;
  episode_number: number;
  season_number: number;
  air_date: string;
  overview: string;
  still_path: string | null;
}

export interface Network {
  id: number;
  name: string;
  logo_path: string | null;
}

export interface Movie {
  id: number;
  title: string;
  poster_path: string | null;
  backdrop_path: string | null;
  overview: string;
  release_date: string;
  vote_average: number;
  runtime?: number;
  status?: string;
}

export interface Person {
  id: number;
  name: string;
  biography: string;
  birthday: string | null;
  deathday: string | null;
  place_of_birth: string | null;
  profile_path: string | null;
  known_for_department: string;
  gender: number;
  popularity: number;
  also_known_as: string[];
  homepage: string | null;
  imdb_id: string | null;
}

export interface CastCredit {
  id: number;
  title?: string;
  name?: string;
  character: string;
  poster_path: string | null;
  backdrop_path: string | null;
  release_date?: string;
  first_air_date?: string;
  vote_average: number;
  media_type: 'movie' | 'tv';
  episode_count?: number;
}

export interface CrewCredit {
  id: number;
  title?: string;
  name?: string;
  job: string;
  department: string;
  poster_path: string | null;
  backdrop_path: string | null;
  release_date?: string;
  first_air_date?: string;
  vote_average: number;
  media_type: 'movie' | 'tv';
  episode_count?: number;
}

export interface PersonCredits {
  cast: CastCredit[];
  crew: CrewCredit[];
}

export interface PersonImage {
  aspect_ratio: number;
  file_path: string;
  height: number;
  width: number;
  vote_average: number;
  vote_count: number;
}

export const tmdb = {
  // Search functions
  searchShows: async (query: string, page = 1) => {
    const { data } = await tmdbApi.get('/search/tv', {
      params: { query, page },
    });
    return data;
  },

  searchMovies: async (query: string, page = 1) => {
    const { data } = await tmdbApi.get('/search/movie', {
      params: { query, page },
    });
    return data;
  },

  searchMulti: async (query: string, page = 1) => {
    const { data } = await tmdbApi.get('/search/multi', {
      params: { query, page },
    });
    return data;
  },

  // TV Show functions
  getShow: async (id: number): Promise<Show> => {
    const { data } = await tmdbApi.get(`/tv/${id}`, {
      params: { append_to_response: 'seasons' },
    });
    return data;
  },

  getShowWithDetails: async (id: number): Promise<Show> => {
    const { data } = await tmdbApi.get(`/tv/${id}`, {
      params: {
        append_to_response: 'seasons,external_ids,videos,credits,recommendations'
      },
    });
    return data;
  },

  getSeason: async (showId: number, seasonNumber: number): Promise<Season> => {
    const { data } = await tmdbApi.get(`/tv/${showId}/season/${seasonNumber}`);
    return data;
  },

  getTrendingShows: async (timeWindow: 'day' | 'week' = 'week') => {
    return cachedRequest(
      'trending_tv',
      'TRENDING',
      async () => {
        const { data } = await tmdbApi.get(`/trending/tv/${timeWindow}`);
        return data;
      },
      timeWindow
    );
  },

  getPopularShows: async (page = 1) => {
    return cachedRequest(
      'popular_tv',
      'POPULAR',
      async () => {
        const { data } = await tmdbApi.get('/tv/popular', { params: { page } });
        return data;
      },
      page
    );
  },

  getAiringToday: async (page = 1) => {
    const { data } = await tmdbApi.get('/tv/airing_today', { params: { page } });
    return data;
  },

  getOnTheAir: async (page = 1) => {
    const { data } = await tmdbApi.get('/tv/on_the_air', { params: { page } });
    return data;
  },

  // Movie functions
  getMovie: async (id: number): Promise<Movie> => {
    const { data } = await tmdbApi.get(`/movie/${id}`);
    return data;
  },

  getTrendingMovies: async (timeWindow: 'day' | 'week' = 'week') => {
    return cachedRequest(
      'trending_movie',
      'TRENDING',
      async () => {
        const { data } = await tmdbApi.get(`/trending/movie/${timeWindow}`);
        return data;
      },
      timeWindow
    );
  },

  getPopularMovies: async (page = 1) => {
    return cachedRequest(
      'popular_movie',
      'POPULAR',
      async () => {
        const { data } = await tmdbApi.get('/movie/popular', { params: { page } });
        return data;
      },
      page
    );
  },

  getTopRatedShows: async (page = 1) => {
    return cachedRequest(
      'top_rated_tv',
      'TOP_RATED',
      async () => {
        const { data } = await tmdbApi.get('/tv/top_rated', { params: { page } });
        return data;
      },
      page
    );
  },

  getTopRatedMovies: async (page = 1) => {
    return cachedRequest(
      'top_rated_movie',
      'TOP_RATED',
      async () => {
        const { data } = await tmdbApi.get('/movie/top_rated', { params: { page } });
        return data;
      },
      page
    );
  },

  getUpcomingMovies: async (page = 1) => {
    // Get NEW movies releasing in the future (not re-releases)
    // Using primary_release_date filters out re-releases, anniversaries, etc.
    // primary_release_date = original world premiere date
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    const sixMonthsFromNowStr = sixMonthsFromNow.toISOString().split('T')[0];

    // Include today's date in cache key so it refreshes daily
    const todayStr = new Date().toISOString().split('T')[0];

    return cachedRequest(
      `upcoming_movie_primary_v3_${todayStr}`,
      'UPCOMING',
      async () => {
        const { data } = await tmdbApi.get('/discover/movie', {
          params: {
            'primary_release_date.gte': tomorrowStr,
            'primary_release_date.lte': sixMonthsFromNowStr,
            sort_by: 'popularity.desc',
            page
          }
        });
        return data;
      },
      page
    );
  },

  getRecentlyReleasedMovies: async (page = 1) => {
    // Get NEW movies released in the past 3 months (not re-releases)
    // Using primary_release_date filters out re-releases, anniversaries, etc.
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    const yesterdayStr = yesterday.toISOString().split('T')[0];
    const threeMonthsAgoStr = threeMonthsAgo.toISOString().split('T')[0];

    // Include today's date in cache key so it refreshes daily
    const todayStr = new Date().toISOString().split('T')[0];

    return cachedRequest(
      `recently_released_movie_primary_v3_${todayStr}`,
      'NOW_PLAYING',
      async () => {
        const { data } = await tmdbApi.get('/discover/movie', {
          params: {
            'primary_release_date.gte': threeMonthsAgoStr,
            'primary_release_date.lte': yesterdayStr,
            sort_by: 'popularity.desc',
            'vote_count.gte': 50,
            page
          }
        });
        return data;
      },
      page
    );
  },

  // Helper functions
  getImageUrl: (path: string | null, size: 'w200' | 'w300' | 'w500' | 'original' = 'w500') => {
    if (!path) return '/placeholder-image.png';
    return `${TMDB_IMAGE_BASE_URL}/${size}${path}`;
  },

  // Additional TV Show functions
  getSimilarShows: async (id: number, page = 1) => {
    const { data } = await tmdbApi.get(`/tv/${id}/similar`, { params: { page } });
    return data;
  },

  getShowReviews: async (id: number, page = 1) => {
    const { data } = await tmdbApi.get(`/tv/${id}/reviews`, { params: { page } });
    return data;
  },

  getShowVideos: async (id: number) => {
    const { data } = await tmdbApi.get(`/tv/${id}/videos`);
    return data;
  },

  getShowWatchProviders: async (id: number) => {
    const { data } = await tmdbApi.get(`/tv/${id}/watch/providers`);
    return data;
  },

  getShowCredits: async (id: number) => {
    const { data } = await tmdbApi.get(`/tv/${id}/credits`);
    return data;
  },

  // Additional Movie functions
  getSimilarMovies: async (id: number, page = 1) => {
    const { data } = await tmdbApi.get(`/movie/${id}/similar`, { params: { page } });
    return data;
  },

  getMovieReviews: async (id: number, page = 1) => {
    const { data } = await tmdbApi.get(`/movie/${id}/reviews`, { params: { page } });
    return data;
  },

  getMovieVideos: async (id: number) => {
    const { data } = await tmdbApi.get(`/movie/${id}/videos`);
    return data;
  },

  getMovieWatchProviders: async (id: number) => {
    const { data } = await tmdbApi.get(`/movie/${id}/watch/providers`);
    return data;
  },

  getMovieCredits: async (id: number) => {
    const { data } = await tmdbApi.get(`/movie/${id}/credits`);
    return data;
  },

  // Check for new seasons/episodes
  checkForNewContent: async (showId: number, lastKnownSeason: number): Promise<{
    hasNewSeason: boolean;
    latestSeason: number;
    newEpisodes?: Episode[];
  }> => {
    try {
      const show = await tmdb.getShow(showId);
      const hasNewSeason = (show.number_of_seasons || 0) > lastKnownSeason;

      return {
        hasNewSeason,
        latestSeason: show.number_of_seasons || lastKnownSeason,
        newEpisodes: show.next_episode_to_air ? [show.next_episode_to_air] : undefined,
      };
    } catch (error) {
      return {
        hasNewSeason: false,
        latestSeason: lastKnownSeason,
      };
    }
  },

  // Discover functions for genre-based content
  discoverMovies: async (params: Record<string, string | number> = {}) => {
    return cachedRequest(
      'discover_movie',
      'POPULAR',
      async () => {
        const { data } = await tmdbApi.get('/discover/movie', { params });
        return data;
      },
      JSON.stringify(params)
    );
  },

  discoverShows: async (params: Record<string, string | number> = {}) => {
    return cachedRequest(
      'discover_tv',
      'POPULAR',
      async () => {
        const { data } = await tmdbApi.get('/discover/tv', { params });
        return data;
      },
      JSON.stringify(params)
    );
  },

  // Discover by keyword ID (for seasonal/holiday movies)
  discoverByKeyword: async (keywordId: number, page = 1) => {
    return cachedRequest(
      'discover_keyword',
      'POPULAR',
      async () => {
        const { data } = await tmdbApi.get('/discover/movie', {
          params: {
            with_keywords: keywordId,
            sort_by: 'popularity.desc',
            page
          }
        });
        return data;
      },
      `${keywordId}_${page}`
    );
  },

  // Search for keywords by name
  searchKeywords: async (query: string) => {
    return cachedRequest(
      'search_keyword',
      'SHORT',
      async () => {
        const { data } = await tmdbApi.get('/search/keyword', {
          params: { query }
        });
        return data;
      },
      query
    );
  },

  // Keywords functions
  getMovieKeywords: async (id: number) => {
    const { data } = await tmdbApi.get(`/movie/${id}/keywords`);
    return data;
  },

  getShowKeywords: async (id: number) => {
    const { data } = await tmdbApi.get(`/tv/${id}/keywords`);
    return data;
  },

  // Genre functions
  getMovieGenres: async () => {
    const { data } = await tmdbApi.get('/genre/movie/list');
    return data;
  },

  getTVGenres: async () => {
    const { data } = await tmdbApi.get('/genre/tv/list');
    return data;
  },

  // Configuration functions
  getLanguages: async () => {
    const { data } = await tmdbApi.get('/configuration/languages');
    return data;
  },

  getCountries: async () => {
    const { data } = await tmdbApi.get('/configuration/countries');
    return data;
  },

  // Additional recommendation functions
  getMovieRecommendations: async (id: number, page = 1) => {
    const { data } = await tmdbApi.get(`/movie/${id}/recommendations`, { params: { page } });
    return data;
  },

  getShowRecommendations: async (id: number, page = 1) => {
    const { data } = await tmdbApi.get(`/tv/${id}/recommendations`, { params: { page } });
    return data;
  },

  // Person/Actor functions
  getPerson: async (id: number): Promise<Person> => {
    const { data } = await tmdbApi.get(`/person/${id}`);
    return data;
  },

  getPersonCombinedCredits: async (id: number): Promise<PersonCredits> => {
    const { data } = await tmdbApi.get(`/person/${id}/combined_credits`);
    return data;
  },

  getPersonMovieCredits: async (id: number) => {
    const { data } = await tmdbApi.get(`/person/${id}/movie_credits`);
    return data;
  },

  getPersonTVCredits: async (id: number) => {
    const { data } = await tmdbApi.get(`/person/${id}/tv_credits`);
    return data;
  },

  getPersonImages: async (id: number) => {
    const { data } = await tmdbApi.get(`/person/${id}/images`);
    return data;
  },

  getPersonExternalIds: async (id: number) => {
    const { data } = await tmdbApi.get(`/person/${id}/external_ids`);
    return data;
  },

  getPopularPeople: async (page = 1) => {
    const { data} = await tmdbApi.get('/person/popular', { params: { page } });
    return data;
  },

  searchPeople: async (query: string, page = 1) => {
    const { data } = await tmdbApi.get('/search/person', {
      params: { query, page },
    });
    return data;
  },
};